/**
 * ProfileView - NVIDIA Profile page view
 *
 * Displays company profile information including business description,
 * stock information, and financial overview
 */

import { BaseView } from './BaseView';
import { DownloadService } from '../services/DownloadService';
import type { ProfileData } from '../types';

export class ProfileView extends BaseView {
	private downloadService: DownloadService;

	constructor() {
		super();
		this.downloadService = new DownloadService();
	}
	/**
	 * Render the profile view content
	 */
	render(): string {
		return `
			<div class="company-header">
				<div class="company-name">NVIDIA Corporation (NasdaqGS:NVDA)</div>
			</div>

			<div class="button-row">
				<div class="tool-button quick-report-button">
					<span class="button-text">Quick Report</span>
				</div>
				<div class="tool-button tearsheet-button">
					<span class="button-text">Tearsheet Report</span>
				</div>
				<div class="tool-button ciq-report-button">
					<span class="button-text">CIQ Report</span>
				</div>
				<div class="tool-button pdf-button" data-pdf="../files/NVIDIA-10-K-08-27-2025.pdf">
					<img src="../img/icon3.png" alt="10-K" onerror="this.src='../img/icon-placeholder.png'">
					<span class="button-text">10-K</span>
				</div>
				<div class="tool-button pdf-button" data-pdf="../files/NVIDIA-10-Q-08-27-2025.pdf">
					<img src="../img/icon4.png" alt="10-Q" onerror="this.src='../img/icon-placeholder.png'">
					<span class="button-text">10-Q</span>
				</div>
				<div class="tool-button pdf-button" data-pdf="../files/NVIDIA-special-call-09-18-2025.pdf">
					<img src="../img/icon5.png" alt="Latest Transcript" onerror="this.src='../img/icon-placeholder.png'">
					<span class="button-text">Latest Transcript</span>
				</div>
			</div>

			<table class="info-table">
				<tr>
					<td width="200">Website:</td>
					<td>www.nvidia.com</td>
					<td colspan="2">Coverage Summary</td>
				</tr>
				<tr>
					<td>Number of Employees</td>
					<td>36,000</td>
					<td>Coverage List:</td>
					<td class="no-text">No</td>
				</tr>
				<tr>
					<td>Ticker:</td>
					<td>NVDA (NasdaqGS)</td>
					<td>Relationships:</td>
					<td class="no-text">No</td>
				</tr>
				<tr>
					<td>Current Professionals Profiled:</td>
					<td>45</td>
					<td>Projects:</td>
					<td class="no-text">No</td>
				</tr>
				<tr>
					<td>Year Founded:</td>
					<td>1993</td>
					<td>Activity Logs/Tasks:</td>
					<td class="no-text">No</td>
				</tr>
			</table>

			<div class="section-header">
				Business Description
			</div>
			<div style="padding: 5px;">
				NVIDIA Corporation, a computing infrastructure company, provides graphics and compute and networking solutions in the United States, Singapore, Taiwan, China, Hong Kong, and internationally. The Compute & Networking segment comprises Data Center computing platforms and end-to-end networking platforms, including Quantum for InfiniBand and Spectrum for Ethernet; NVIDIA DRIVE automated-driving platform and automotive development agreements; Jetson embedded and other software platforms; NVIDIA AI Enterprise and other software, and DGX Cloud software and services. The Graphics segment offers GeForce GPUs for gaming and PCs, the GeForce NOW game streaming service and related infrastructure, and solutions for gaming platforms; Quadro/NVIDIA RTX GPUs for enterprise workstation graphics; virtual GPU or vGPU software for cloud-based visual and virtual computing; automotive platforms for infotainment systems; and Omniverse software for building and operating industrial AI and digital twin applications. It also customizes gigantic solutions designed in collaboration with NVIDIA to accelerate enterprise AI adoption. The company's products are used in gaming, professional visualization, data center, and automotive markets. It sells its products to original equipment manufacturers, original device manufacturers, system integrators and distributors, independent software vendors, cloud service providers, consumer internet companies, add-in board manufacturers, distributors, automotive manufacturers and tier-1 automotive suppliers, and other ecosystem participants. NVIDIA Corporation was incorporated in 1993 and is headquartered in Santa Clara, California.
			</div>

			<div class="section-header">
				Primary Industry Classification
			</div>
			<div style="padding: 5px;">
				Semiconductors
			</div>

			<div class="section-header">
				Primary Office Location
			</div>
			<div style="padding: 5px;">
				2788 San Tomas Expressway | Santa Clara, CA | 95051 | United States<br>
				Phone: (408) 486-2000
			</div>

			<div class="section-header">
				Current and Pending Investors
			</div>
			<div style="padding: 5px;">
				Jk&B Management V, L.P., Keskimainen Eläkevakuutusyhtiö Ilmarinen, Keskinäinen Työeläkevakuutusyhtiö Elo, Keskinainen työeläkevakuutusyhtiö Varma, Temasek Holdings (Private) Limited, The Vanguard Group, Inc., VentureTech Alliance LLC
			</div>

			<div class="section-header">
				Prior Investors
			</div>
			<div style="padding: 5px;">
				Comdisco Ventures, deVinci Capital Group, El Dorado Ventures, Icon Management Associates LLC, ITOCHU Techno-Solutions America, Inc., Lighthouse Capital Partners, Rally Ventures, Rocket Ventures, SB Investment Advisers (UK) Limited, Sequoia Capital Franchise Fund, L.P., Sequoia Capital IX Fund, L.P., Sequoia Capital Operations LLC, Sequoia Capital VI, L.P., Sequoia Capital X, L.P., SoftBank Vision Fund L.P., Sutter Hill Ventures, Veritas Gaither, Tench Coxe, Worldview Technology Partners I, L.P., WorldView Technology Partners, Inc.
			</div>

			<div class="section-header">
				Investment Arms
			</div>
			<div style="padding: 5px;">
				GPU Ventures, NVentures LLC
			</div>

			<div class="section-header">
				Stock Quote and Chart (Currency: USD)
			</div>
			<table class="info-table">
				<tr>
					<td width="222">Last (Delayed)</td>
					<td width="222">109.67</td>
					<td width="222">Market Cap (mm)</td>
					<td width="222" style="text-align: right;">2,675,948.0</td>
					<td rowspan="9" style="padding: 0;">
						<div style="width: 100%; height: 100%;">
							<img src="../img/stock-chart.png" alt="NVIDIA Stock Chart" style="width: 100%; height: 100%;" onerror="this.src='../img/placeholder.png'">
						</div>
					</td>
				</tr>
				<tr>
					<td>Open</td>
					<td>111.49</td>
					<td>Shares Out. (mm)</td>
					<td style="text-align: right;">24,400.0</td>
				</tr>
				<tr>
					<td>Previous Close</td>
					<td>109.67</td>
					<td>Float %</td>
					<td style="text-align: right; color: blue;">96.0%</td>
				</tr>
				<tr>
					<td>Change on Day</td>
					<td style="color: red;">(1.76)</td>
					<td>Shares Sold Short (mm)</td>
					<td style="text-align: right;">258.1</td>
				</tr>
				<tr>
					<td>Change % on Day</td>
					<td style="color: red;">(1.6)%</td>
					<td>Dividend Yield %</td>
					<td style="text-align: right;">0.0%</td>
				</tr>
				<tr>
					<td>Day High/Low</td>
					<td>112.87/ 109.07</td>
					<td>Diluted EPS Excl. Extra Items</td>
					<td style="text-align: right;">2.94</td>
				</tr>
				<tr>
					<td>52 wk High/Low</td>
					<td>153.13/ 75.61</td>
					<td>P/Diluted EPS Before Extra</td>
					<td style="text-align: right;">37.30x</td>
				</tr>
				<tr>
					<td>Volume (mm)</td>
					<td>229.87</td>
					<td>Avg 3M Dly Vlm (mm)</td>
					<td style="text-align: right;">274.30</td>
				</tr>
				<tr>
					<td>Beta 5Y</td>
					<td>1.76</td>
					<td></td>
					<td></td>
				</tr>
			</table>
			<div style="padding: 5px; background-color: #f2f2f2; border: 1px solid #ccc; border-top: none;">
				Delayed Quote** | Last Updated on Mar-28-2025 12:00 AM (GMT-5)
				<div style="float: right;">
					<select style="width: 350px;">
						<option selected>NasdaqGS:NVDA - Common Stock</option>
					</select>
					<button>Go</button>
				</div>
			</div>

			<div class="section-header">
				Financial Information (Currency: USD, in mm) <a href="/financials" class="view-link">View Details</a>
			</div>
			<table class="info-table">
				<tr>
					<td width="222">Total Revenue</td>
					<td width="222" style="text-align: right; color: blue;">130,497.0</td>
					<td width="222">Market Capitalization</td>
					<td width="222" style="text-align: right;">2,675,948.0</td>
					<td width="222">TEV/Total Revenue</td>
					<td width="222" style="text-align: right;">20.3x</td>
				</tr>
				<tr>
					<td>EBITDA</td>
					<td style="text-align: right; color: blue;">83,317.0</td>
					<td>Total Enterprise Value</td>
					<td style="text-align: right;">2,643,008.0</td>
					<td>TEV/EBITDA</td>
					<td style="text-align: right;">31.6x</td>
				</tr>
				<tr>
					<td>EBIT</td>
					<td style="text-align: right; color: blue;">81,453.0</td>
					<td>Cash & ST Invst.</td>
					<td style="text-align: right;">43,210.0</td>
					<td>P/Diluted EPS Before Extra</td>
					<td style="text-align: right;">37.3x</td>
				</tr>
				<tr>
					<td>Net Income</td>
					<td style="text-align: right; color: blue;">72,880.0</td>
					<td>Total Debt</td>
					<td style="text-align: right;">10,270.0</td>
					<td>Price/Tang BV</td>
					<td style="text-align: right;">36.6x</td>
				</tr>
				<tr>
					<td>Capital Expenditure</td>
					<td style="text-align: right; color: red;">(3,236.0)</td>
					<td>Total Assets</td>
					<td style="text-align: right;">111,601.0</td>
					<td>Total Debt/EBITDA</td>
					<td style="text-align: right;">0.1x</td>
				</tr>
			</table>
			<div style="padding: 5px; background-color: #f2f2f2; border: 1px solid #ccc; border-top: none;">
				<strong>Latest Annual:</strong>
				<a href="/income-statement">Income Statement</a>
				<a href="/balance-sheet">Balance Sheet</a>
				<a href="/cash-flow">Cash Flow</a>
				<strong>Latest Interim:</strong>
				<a href="/income-statement">Income Statement</a>
				<a href="/balance-sheet">Balance Sheet</a>
				<a href="/cash-flow">Cash Flow</a>
				<br>
				Currency in USD in mm, LTM as of Jan-26-2025 TEV and Market Cap are calculated using a close price as of Mar-28-2025
			</div>
		`;
	}

	/**
	 * Lifecycle hook - called after view is mounted
	 */
	protected onMount(): void {
		this.setupPdfButtons();
		this.setupDownloadButtons();
	}

	/**
	 * Set up PDF button click handlers
	 */
	private setupPdfButtons(): void {
		const pdfButtons = this.querySelectorAll<HTMLElement>('.pdf-button');

		pdfButtons.forEach((button) => {
			this.addEventListener(button, 'click', () => {
				const pdfPath = button.getAttribute('data-pdf');
				if (pdfPath) {
					window.open(pdfPath, '_blank');
				}
			});
		});
	}

	/**
	 * Set up download button handlers for PDF/RTF reports
	 */
	private setupDownloadButtons(): void {
		// Quick Report button - Downloads 1-page summary PDF
		const quickReportButton = this.querySelector<HTMLElement>('.quick-report-button');
		if (quickReportButton) {
			this.addEventListener(quickReportButton, 'click', () => {
				const profileData = this.extractProfileData();
				this.downloadService.downloadQuickReportPDF(profileData);
			});
		}

		// Tearsheet Report button - Downloads comprehensive PDF
		const tearsheetButton = this.querySelector<HTMLElement>('.tearsheet-button');
		if (tearsheetButton) {
			this.addEventListener(tearsheetButton, 'click', () => {
				const profileData = this.extractProfileData();
				this.downloadService.downloadTearsheetPDF(profileData);
			});
		}

		// CIQ Report button - Downloads RTF (legacy)
		const ciqReportButton = this.querySelector<HTMLElement>('.ciq-report-button');
		if (ciqReportButton) {
			this.addEventListener(ciqReportButton, 'click', () => {
				this.downloadService.downloadCIQReportRTF();
			});
		}
	}

	/**
	 * Extract profile data from the rendered view
	 */
	private extractProfileData(): ProfileData {
		return {
			// Company header
			companyName: 'NVIDIA Corporation',
			ticker: 'NVDA',
			exchange: 'NasdaqGS',

			// Basic information
			website: 'www.nvidia.com',
			numberOfEmployees: '36,000',
			yearFounded: '1993',
			currentProfessionalProfiled: '45',

			// Coverage summary
			coverageList: 'No',
			relationships: 'No',
			projects: 'No',
			activityLogsTasks: 'No',

			// Descriptions and classifications
			businessDescription: 'NVIDIA Corporation, a computing infrastructure company, provides graphics and compute and networking solutions in the United States, Singapore, Taiwan, China, Hong Kong, and internationally. The Compute & Networking segment comprises Data Center computing platforms and end-to-end networking platforms, including Quantum for InfiniBand and Spectrum for Ethernet; NVIDIA DRIVE automated-driving platform and automotive development agreements; Jetson embedded and other software platforms; NVIDIA AI Enterprise and other software, and DGX Cloud software and services. The Graphics segment offers GeForce GPUs for gaming and PCs, the GeForce NOW game streaming service and related infrastructure, and solutions for gaming platforms; Quadro/NVIDIA RTX GPUs for enterprise workstation graphics; virtual GPU or vGPU software for cloud-based visual and virtual computing; automotive platforms for infotainment systems; and Omniverse software for building and operating industrial AI and digital twin applications. It also customizes gigantic solutions designed in collaboration with NVIDIA to accelerate enterprise AI adoption. The company\'s products are used in gaming, professional visualization, data center, and automotive markets. It sells its products to original equipment manufacturers, original device manufacturers, system integrators and distributors, independent software vendors, cloud service providers, consumer internet companies, add-in board manufacturers, distributors, automotive manufacturers and tier-1 automotive suppliers, and other ecosystem participants. NVIDIA Corporation was incorporated in 1993 and is headquartered in Santa Clara, California.',
			primaryIndustry: 'Semiconductors',

			// Location
			officeLocation: '2788 San Tomas Expressway | Santa Clara, CA | 95051 | United States',
			phone: '(408) 486-2000',

			// Investors
			currentPendingInvestors: 'Jk&B Management V, L.P., Keskimainen Eläkevakuutusyhtiö Ilmarinen, Keskinäinen Työeläkevakuutusyhtiö Elo, Keskinainen työeläkevakuutusyhtiö Varma, Temasek Holdings (Private) Limited, The Vanguard Group, Inc., VentureTech Alliance LLC',
			priorInvestors: 'Comdisco Ventures, deVinci Capital Group, El Dorado Ventures, Icon Management Associates LLC, ITOCHU Techno-Solutions America, Inc., Lighthouse Capital Partners, Rally Ventures, Rocket Ventures, SB Investment Advisers (UK) Limited, Sequoia Capital Franchise Fund, L.P., Sequoia Capital IX Fund, L.P., Sequoia Capital Operations LLC, Sequoia Capital VI, L.P., Sequoia Capital X, L.P., SoftBank Vision Fund L.P., Sutter Hill Ventures, Veritas Gaither, Tench Coxe, Worldview Technology Partners I, L.P., WorldView Technology Partners, Inc.',
			investmentArms: 'GPU Ventures, NVentures LLC',

			// Stock and financial data
			stockQuote: {
				lastDelayed: '109.67',
				marketCapMm: '2,675,948.0',
				open: '111.49',
				sharesOutMm: '24,400.0',
				previousClose: '109.67',
				floatPercent: '96.0%',
				changeOnDay: '(1.76)',
				sharesSoldShortMm: '258.1',
				changePercentOnDay: '(1.6)%',
				dividendYieldPercent: '0.0%',
				dayHighLow: '112.87/ 109.07',
				dilutedEpsExclExtraItems: '2.94',
				weekHighLow52: '153.13/ 75.61',
				pDilutedEpsBeforeExtra: '37.30x',
				volumeMm: '229.87',
				avg3MDlyVlmMm: '274.30',
				beta5Y: '1.76',
				lastUpdated: 'Delayed Quote** | Last Updated on Mar-28-2025 12:00 AM (GMT-5)',
				stockType: 'NasdaqGS:NVDA - Common Stock',
			},

			financialInfo: {
				totalRevenue: '130,497.0',
				marketCapitalization: '2,675,948.0',
				tevTotalRevenue: '20.3x',
				ebitda: '83,317.0',
				totalEnterpriseValue: '2,643,008.0',
				tevEbitda: '31.6x',
				ebit: '81,453.0',
				cashStInvst: '43,210.0',
				pDilutedEpsBeforeExtra: '37.3x',
				netIncome: '72,880.0',
				totalDebt: '10,270.0',
				priceTangBv: '36.6x',
				capitalExpenditure: '(3,236.0)',
				totalAssets: '111,601.0',
				totalDebtEbitda: '0.1x',
				latestAnnualDate: 'Income Statement, Balance Sheet, Cash Flow',
				latestInterimDate: 'Income Statement, Balance Sheet, Cash Flow',
				ltmAsOf: 'Jan-26-2025',
				marketCapAsOf: 'Mar-28-2025',
			},

			// Optional chart image
			stockChartUrl: '../img/stock-chart.png',
		};
	}
}
